// Coordinate-aware PDF bank statement parser.
// PDF text is not a table: preserving x/y positions is required to keep references,
// amounts and balances from being concatenated into one number.

const bankPdfNormalize = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const bankPdfParseAmount = (value, fallback = NaN) => {
  let text = String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/^\s*(?:NOK|kr)\s*/i, '')
    .replace(/\s*(?:NOK|kr)\s*$/i, '')
    .replace(/\s/g, '')
    .replace(/[^\d,.-]/g, '');
  if (!text) return fallback;
  if (text.includes(',')) text = text.replace(/\./g, '').replace(',', '.');
  else if ((text.match(/\./g) || []).length > 1) {
    const decimalIndex = text.lastIndexOf('.');
    text = `${text.slice(0, decimalIndex).replace(/\./g, '')}${text.slice(decimalIndex)}`;
  }
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const bankPdfParseDate = (value) => {
  const match = String(value || '').trim().match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (!match) return '';
  const year = match[3].length === 2 ? `20${match[3]}` : match[3];
  return `${year}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
};

const bankPdfIsAmount = (value) => {
  const text = String(value || '').trim().replace(/^\s*(?:NOK|kr)\s*/i, '').replace(/\s*(?:NOK|kr)\s*$/i, '');
  return /^[+-]?(?:\d+|\d{1,3}(?:[ .]\d{3})+),\d{2}$/.test(text);
};

// pdf.js may return both DNB date columns as one text item (for example
// "02.01.25 02.01.25"). Treat dates as tokens inside the item instead of
// requiring one PDF item per date.
const bankPdfDateTokens = (value) => String(value || '')
  .match(/\b\d{1,2}[./-]\d{1,2}[./-]\d{2,4}\b/g)
  ?.map(bankPdfParseDate)
  .filter(Boolean) || [];

const bankPdfRows = (items) => {
  const sorted = (items || [])
    .filter((item) => String(item?.text || '').trim())
    .map((item) => ({
      text: String(item.text).trim(),
      page: Number(item.page) || 1,
      x: Number(item.x) || 0,
      y: Number(item.y) || 0,
    }))
    .sort((a, b) => a.page - b.page || b.y - a.y || a.x - b.x);
  const rows = [];
  sorted.forEach((item) => {
    const previous = rows[rows.length - 1];
    if (!previous || previous.page !== item.page || Math.abs(previous.y - item.y) > 2) {
      rows.push({ page: item.page, y: item.y, items: [item] });
    } else {
      previous.items.push(item);
      previous.y = (previous.y + item.y) / 2;
    }
  });
  return rows.map((row) => ({
    ...row,
    items: row.items.sort((a, b) => a.x - b.x),
    text: row.items.sort((a, b) => a.x - b.x).map((item) => item.text).join(' ').replace(/\s+/g, ' ').trim(),
  }));
};

// Kolonneposisjonen finnes fra overskriftsordet uansett hvordan PDF-en har delt
// teksten. pdf.js sender noen ganger «Ut av konto» som tre tekstbiter og andre
// ganger som en. Kravet om en bit som var noyaktig «ut» gjorde at hele
// tabellgjenkjenningen falt bort pa den andre varianten — og utskriften ble
// importert uten en eneste linje, uten at noe sa fra.
const bankPdfColumnX = (row, word) => row.items.find((item) => {
  const text = bankPdfNormalize(item.text);
  return text === word || text.startsWith(`${word} `);
})?.x;

const bankPdfColumnLayouts = (rows) => rows.map((row) => {
  const normalized = bankPdfNormalize(row.text);
  if (!normalized.includes('dato') || !normalized.includes('forklarende tekst') || !normalized.includes('ut av konto') || !normalized.includes('inn pa konto')) return null;
  const descriptionX = bankPdfColumnX(row, 'forklarende');
  const outgoingX = bankPdfColumnX(row, 'ut');
  const incomingX = bankPdfColumnX(row, 'inn');
  const interestDateX = bankPdfColumnX(row, 'rentedato');
  if (![descriptionX, outgoingX, incomingX, interestDateX].every(Number.isFinite)) return null;
  return { page: row.page, headerY: row.y, descriptionX, outgoingX, incomingX, interestDateX };
}).filter(Boolean);

const bankPdfExplicitBalance = (rows, patterns) => {
  for (const row of rows) {
    const normalized = bankPdfNormalize(row.text);
    if (!patterns.some((pattern) => pattern.test(normalized))) continue;
    const amountItem = [...row.items].reverse().find((item) => bankPdfIsAmount(item.text));
    if (amountItem) return bankPdfParseAmount(amountItem.text);
  }
  return undefined;
};

const bankPdfExplicitDate = (rows, patterns) => {
  for (const row of rows) {
    const normalized = bankPdfNormalize(row.text);
    if (!patterns.some((pattern) => pattern.test(normalized))) continue;
    const dates = bankPdfDateTokens(row.text);
    if (dates.length) return dates[dates.length - 1];
  }
  return undefined;
};

const bankPdfNextDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return '';
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

const bankPdfCoveredMonthEnd = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return value;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  const monthEnd = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
  for (let cursor = new Date(date.getTime() + 86400000); cursor <= monthEnd; cursor = new Date(cursor.getTime() + 86400000)) {
    if (cursor.getUTCDay() !== 0 && cursor.getUTCDay() !== 6) return value;
  }
  return monthEnd.toISOString().slice(0, 10);
};

const bankPdfDescription = (row, layout) => row.items
  .filter((item) => item.x >= layout.descriptionX - 5 && item.x < layout.outgoingX)
  .map((item) => item.text)
  .join(' ')
  .replace(/\s+/g, ' ')
  .trim();

const bankPdfTableLines = (rows, layouts, fileName) => {
  const lines = [];
  layouts.forEach((layout) => {
    const pageRows = rows.filter((row) => row.page === layout.page && row.y < layout.headerY - 3);
    let current = null;
    for (const row of pageRows) {
      const normalized = bankPdfNormalize(row.text);
      // Section footers start left of the description column. The same words
      // inside a payment description must not truncate the remaining statement.
      const startsBeforeDescription = row.items.some((item) => item.x < layout.descriptionX - 5);
      if (startsBeforeDescription && /^(renter,? gebyrer|rapportering|summering|totalt)/.test(normalized)) break;
      const dateTokens = row.items
        .filter((item) => item.x < layout.descriptionX)
        .flatMap((item) => bankPdfDateTokens(item.text));
      const outgoingItem = row.items.find((item) => item.x >= layout.outgoingX && item.x < layout.incomingX && bankPdfIsAmount(item.text));
      const incomingItem = row.items.find((item) => item.x >= layout.incomingX && item.x < layout.interestDateX && bankPdfIsAmount(item.text));
      if (dateTokens.length && (outgoingItem || incomingItem)) {
        const outgoing = outgoingItem ? bankPdfParseAmount(outgoingItem.text, 0) : 0;
        const incoming = incomingItem ? bankPdfParseAmount(incomingItem.text, 0) : 0;
        const bookingDate = dateTokens[1] || dateTokens[0];
        current = {
          id: `bank-line-${Date.now()}-${lines.length + 1}`,
          date: bookingDate,
          description: bankPdfDescription(row, layout) || fileName || 'Banktransaksjon',
          amount: incoming - outgoing,
        };
        if (current.date && current.amount) lines.push(current);
        else current = null;
        continue;
      }
      if (!current || row.items.some((item) => item.x < layout.descriptionX - 5)) continue;
      const continuation = bankPdfDescription(row, layout);
      if (continuation) current.description = `${current.description} ${continuation}`.replace(/\s+/g, ' ').trim();
    }
  });
  return lines;
};

const bankPdfSignedFallbackLines = (rows, fileName) => rows.map((row, index) => {
  const normalized = bankPdfNormalize(row.text);
  if (/saldo|balance/.test(normalized)) return null;
  const dates = row.items.flatMap((item) => bankPdfDateTokens(item.text));
  const amountItem = row.items.find((item) => /^[+-]/.test(item.text.trim()) && bankPdfIsAmount(item.text));
  if (!dates.length || !amountItem) return null;
  const amount = bankPdfParseAmount(amountItem.text);
  if (!Number.isFinite(amount) || !amount) return null;
  return {
    id: `bank-line-${Date.now()}-${index + 1}`,
    date: dates[1] || dates[0],
    description: row.items.filter((item) => item !== amountItem && !bankPdfDateTokens(item.text).length).map((item) => item.text).join(' ').trim() || fileName || 'Banktransaksjon',
    amount,
  };
}).filter(Boolean);

// Utskriften inneholder sin egen fasit: inngaaende saldo pluss bevegelsene skal gi
// utgaaende saldo. Stemmer ikke det, har avlesningen tatt feil et sted — en linje
// mangler, et belop havnet i feil kolonne, eller en saldo ble lest fra feil rad.
//
// Dette er den eneste kontrollen som er uavhengig av bank og PDF-generator, og
// derfor den som fanger de feilene ingen mal er skrevet for. Uten den ble en
// halvlest utskrift importert helt stille, og feilen dukket forst opp som et
// uforklarlig avvik langt inne i avstemmingen.
const bankPdfBalanceCheck = ({ lines, openingBalance, closingBalance }) => {
  if (!Number.isFinite(openingBalance) || !Number.isFinite(closingBalance)) {
    return { status: 'unknown', reason: 'missing-balance' };
  }
  const lineTotal = lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const expected = Math.round((openingBalance + lineTotal) * 100) / 100;
  const difference = Math.round((closingBalance - expected) * 100) / 100;
  if (Math.abs(difference) < 0.005) return { status: 'ok', difference: 0, lineTotal };
  return {
    status: 'mismatch',
    reason: lines.length ? 'line-total-mismatch' : 'no-lines-parsed',
    difference,
    lineTotal,
    expectedClosingBalance: expected,
  };
};

const parsePdfBankTextItems = (items, fileName = 'Bankutskrift') => {
  const rows = bankPdfRows(items);
  const layouts = bankPdfColumnLayouts(rows);
  const lines = layouts.length ? bankPdfTableLines(rows, layouts, fileName) : bankPdfSignedFallbackLines(rows, fileName);
  const dates = lines.map((line) => line.date).filter(Boolean).sort();
  const openingBalance = bankPdfExplicitBalance(rows, [/saldo forrige utskrift/, /inng(?:a|aa)ende saldo/, /opening balance/]);
  const closingBalance = bankPdfExplicitBalance(rows, [/ny saldo per/, /utg(?:a|aa)ende saldo/, /closing balance/]);
  const previousStatementDate = bankPdfExplicitDate(rows, [/saldo forrige utskrift/, /previous statement balance/]);
  const closingBalanceDate = bankPdfExplicitDate(rows, [/ny saldo per/, /closing balance/]);
  const statementPeriodFrom = bankPdfNextDate(previousStatementDate);
  const hasExplicitPeriod = statementPeriodFrom && closingBalanceDate && statementPeriodFrom <= closingBalanceDate;
  // DNB monthly statements can date the closing balance on the last banking day
  // even though the statement covers the entire calendar month. Extend only a
  // weekend-only tail; a missing weekday remains a partial period for review.
  const statementPeriodTo = hasExplicitPeriod
    ? (layouts.length ? bankPdfCoveredMonthEnd(closingBalanceDate) : closingBalanceDate)
    : '';
  return {
    lines,
    periodFrom: hasExplicitPeriod ? statementPeriodFrom : dates[0],
    periodTo: hasExplicitPeriod ? statementPeriodTo : dates[dates.length - 1],
    openingBalance,
    closingBalance,
    balanceCheck: bankPdfBalanceCheck({ lines, openingBalance, closingBalance }),
  };
};

const readPdfBankStatementFile = async (file) => {
  await window.loadPdfJsLib().catch(() => {});
  if (!window.pdfjsLib?.getDocument) throw new Error('PDF-parseren er ikke lastet inn.');
  // isEvalSupported: false stenger eval-stien i pdf.js (CVE-2024-4367) — bankutskrifter er brukeropplastet.
  const pdf = await window.pdfjsLib.getDocument({ data: new Uint8Array(await file.arrayBuffer()), isEvalSupported: false }).promise;
  const items = [];
  for (let pageNo = 1; pageNo <= pdf.numPages; pageNo += 1) {
    const page = await pdf.getPage(pageNo);
    const content = await page.getTextContent();
    (content.items || []).forEach((item) => items.push({
      text: String(item.str || '').trim(),
      page: pageNo,
      x: Number(item.transform?.[4] || 0),
      y: Number(item.transform?.[5] || 0),
    }));
  }
  return parsePdfBankTextItems(items, file.name || 'Bankutskrift');
};

window.PlummeBankPdfParser = { parsePdfBankTextItems, readPdfBankStatementFile };
